import type {
  ConditionOperator,
  FindUrlParamsQuery,
} from "schemas/generated/analyticsRead";

import * as React from "react";

import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { useCreatableComboboxInputOptions } from "@v1/components/designSystem/combobox/hooks/useCreatableComboboxInputOptions";
import Popover from "@v1/components/designSystem/popover/Popover";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import {
  createUrlParamsFilterOptions,
  OPERATOR_OPTIONS,
} from "@v1/features/analytics/moreFilters/constants";
import { findLabelForValue } from "@v1/features/analytics/moreFilters/utils";
import useFindUrlParams from "@v1/features/analytics/useFindUrlParams";
import { CornerDownRight, Search } from "lucide-react";

type BarPopoverProps = {
  selectedUrlParamOptionValue: string;
  isOpen: boolean;
  onOpenChange(isOpen: boolean): void;
};

const INITIAL_SEARCH_VALUE = "";
const INITIAL_OPERATOR_VALUE = "equals";
const INITIAL_SEARCH_LIMIT = 20;
const INITIAL_SEARCH_OFFSET = 0;
const INITIAL_SEARCH_ORDER = "DESC";

function createOptionFromValue(value: string) {
  return {
    label: value,
    value,
  };
}

const BarPopover: React.FC<BarPopoverProps> = ({
  selectedUrlParamOptionValue,
  isOpen,
  onOpenChange,
}) => {
  const { query, dispatchAnalyticsQuery } = useAnalyticsQueryContext();
  const [searchValue, setSearchValue] = React.useState(INITIAL_SEARCH_VALUE);
  const [operatorValue, setOperatorValue] = React.useState<ConditionOperator>(
    INITIAL_OPERATOR_VALUE,
  );

  const urlHosts = query.urlHosts ?? [];

  const handleSearchSelectValueChange = (value: string) => {
    dispatchAnalyticsQuery({
      type: "filters.addUrlParam",
      payload: {
        attribute: selectedUrlParamOptionValue,
        operator: operatorValue,
        value,
      },
    });

    setSearchValue(value);

    resetState();
    onOpenChange(false);
  };

  const resetState = () => {
    setSearchValue(INITIAL_SEARCH_VALUE);
    setOperatorValue(INITIAL_OPERATOR_VALUE);
  };

  const findUrlParamsQuery: FindUrlParamsQuery = React.useMemo(() => {
    return {
      urlHosts,
      order: INITIAL_SEARCH_ORDER,
      limit: INITIAL_SEARCH_LIMIT,
      offset: INITIAL_SEARCH_OFFSET,
      key: selectedUrlParamOptionValue,
      value: searchValue,
    };
  }, [urlHosts, selectedUrlParamOptionValue, searchValue]);

  const { urlParams: findUrlParamsResponse } =
    useFindUrlParams(findUrlParamsQuery);

  const urlParamsOptionsFromResponse =
    findUrlParamsResponse?.map((urlParam) => createOptionFromValue(urlParam)) ??
    [];

  const [urlParamsInput, setUrlParamsInput, urlParamsOptions] =
    useCreatableComboboxInputOptions(urlParamsOptionsFromResponse);

  return (
    <Popover.Root
      isOpen={isOpen}
      onOpenChange={() => {
        onOpenChange(!isOpen);
        resetState();
      }}
    >
      <div className="absolute left-0 top-0 h-0 w-0">
        <Popover.Anchor className="relative" />
      </div>
      <Popover.Content
        hideCloseButton={true}
        shouldPreventDefaultOnInteractOutside={false}
        side="bottom"
        sideOffset={35}
        className="border-0.5 border-border w-fit"
        align="start"
      >
        <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
          <div className="flex items-center gap-2">
            <CornerDownRight className="text-muted h-5 w-5" />
            <span className="text-medium flex items-center whitespace-nowrap p-2 text-xs">
              {findLabelForValue(
                createUrlParamsFilterOptions(),
                selectedUrlParamOptionValue,
              )}
            </span>
          </div>
          <div>
            <Combobox
              options={OPERATOR_OPTIONS}
              value={operatorValue}
              onChange={(operator) => {
                setOperatorValue(operator as ConditionOperator);
              }}
              trigger={
                <Combobox.SelectionButton
                  title={
                    OPERATOR_OPTIONS.find((opt) => opt.value === operatorValue)
                      ?.label ?? operatorValue
                  }
                  layoutClassName="min-w-[125px]"
                />
              }
            />
          </div>

          <div className="min-w-[300px] md:ml-2">
            <Combobox.Root
              options={urlParamsOptions}
              value={searchValue}
              onChange={handleSearchSelectValueChange}
              input={urlParamsInput}
              onInputChange={(input) => {
                setUrlParamsInput(input);
              }}
            >
              <Combobox.Trigger>
                <Combobox.Input
                  placeholder="Search..."
                  startEnhancer={<Search className="h-3 w-3" />}
                  outlineOnActive={false}
                />
              </Combobox.Trigger>
              <Combobox.Popover>
                <Combobox.Content emptySearchMessage="No matches found." />
              </Combobox.Popover>
            </Combobox.Root>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

export default BarPopover;
